import { Flex } from '@chakra-ui/react';
import { Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Callout, IconButton, Stack } from '@main/complykit';
import { Card } from '@main/complykit/components/card';
import { Content } from '@main/complykit/components/slots';
import { Switch } from '@main/complykit/components/switch';
import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { IntegrationAuthType, IntegrationConnector } from '@main/integrations';
import { getRegisteredIntegrationConnector } from '@main/integrations/registry/connectors';
import { useAlertDialog } from '@main/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ConnectorConfigCollapse,
  type ConnectorConfigPersistence,
  useConnectedConnectorConfigEditor,
} from '../connector-config-collapse';
import { IntegrationDetails } from '../integration-details';
import { IntegrationLogo } from '../integration-logo';

export type BackofficeConnectorCardProps = {
  providerName: Integration_Names_Enum;
  connectorName?: string;
  authName: Integration_Auth_Names_Enum;
  controlsCount: number;
  evidenceCount: number;
  configPersistence: ConnectorConfigPersistence;
  isDisabled: boolean;
  isConfigurableByUser: boolean;
  onConfigurableByUserChange: (isConfigurableByUser: boolean) => void;
  onDisabledChange: (disabled: boolean) => void;
  onDelete: () => void;
};

export const BackofficeConnectorCard = ({
  providerName,
  connectorName,
  authName,
  controlsCount,
  evidenceCount,
  configPersistence,
  isDisabled,
  isConfigurableByUser,
  onDisabledChange,
  onConfigurableByUserChange,
  onDelete,
}: BackofficeConnectorCardProps) => {
  const { t } = useTranslation('integrations');
  const helpMessage = t('usageStatsMessage', { controlsCount, evidenceCount });

  const { openDialog } = useAlertDialog();
  const [hasSavedOAuthConfig, setHasSavedOAuthConfig] = useState(false);
  const connector = getRegisteredIntegrationConnector(providerName);
  const auth = IntegrationConnector.getAuthByName(connector, authName);
  const requiresReconnection = auth.type === IntegrationAuthType.Oauth2Code;
  const configEditor = useConnectedConnectorConfigEditor({
    load: configPersistence.load,
    save: async (value) => {
      if (requiresReconnection) {
        await openDialog({
          dialogHeader: t('configureForm.oauthSave.confirmHeader'),
          dialogContent: t('configureForm.oauthSave.warning'),
          cancelBtnLabel: t('configureForm.cancelBtn'),
          confirmAction: { children: t('configureForm.oauthSave.confirmBtn') },
        });
      }

      const state = await configPersistence.save(value);
      if (requiresReconnection) setHasSavedOAuthConfig(true);
      return state;
    },
  });

  return (
    <Card>
      <Content>
        <Stack gap={4}>
          <Flex align="start" gap={4}>
            <IntegrationLogo name={providerName} />
            <Flex grow={1} minW={0}>
              <IntegrationDetails
                providerName={providerName}
                connectorName={connectorName}
                helpMessage={helpMessage}
              />
            </Flex>
            <Flex direction="column" alignItems="flex-end" flexShrink={0} gap={4}>
              <Flex gap={2}>
                <IconButton
                  label={t('configureForm.configureBtn')}
                  icon={Cog6ToothIcon}
                  variant="soft"
                  isPending={configEditor.isLoading}
                  onPress={() => void configEditor.toggle()}
                />
                <IconButton
                  variant="outline"
                  tone="critical"
                  label={t('deleteConnectorDialog.confirmBtn')}
                  icon={TrashIcon}
                  onPress={onDelete}
                />
              </Flex>
              <Flex direction="column" align="end" gap={2}>
                <Switch
                  isSelected={!isDisabled}
                  onChange={(isSelected) => onDisabledChange(!isSelected)}
                >
                  {t('connectorAdmin.enabled')}
                </Switch>
                <Switch isSelected={isConfigurableByUser} onChange={onConfigurableByUserChange}>
                  {t('connectorAdmin.configurableInApp')}
                </Switch>
              </Flex>
            </Flex>
          </Flex>
          {requiresReconnection && (configEditor.isOpen || hasSavedOAuthConfig) && (
            <Callout status="warning">
              {t(
                hasSavedOAuthConfig
                  ? 'configureForm.oauthSave.saved'
                  : 'configureForm.oauthSave.warning',
              )}
            </Callout>
          )}
        </Stack>
        <ConnectorConfigCollapse
          editor={configEditor}
          t={t}
          tKey={`integrations.${providerName}.connectors.${authName}.config`}
          configSchema={auth.configSchema}
          encryptedConfigSchema={auth.encryptedConfigSchema}
          submitLabel={t('configureForm.saveBtn')}
        />
      </Content>
    </Card>
  );
};
